import React, { useState } from "react";
import { usePageData } from "../context/PageDataContext";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { confirmDelete, successToast, errorToast } from "../utils/dialog";

export default function AdminPageManager() {
  const { pages, addPage, updatePage, deletePage } = usePageData();
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    content: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editingPage, setEditingPage] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, content: value || "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      errorToast("Title is required.");
      return;
    }

    try {
      if (editingPage) {
        await updatePage(editingPage.id, formData, imageFile);
        successToast("Page updated.");
      } else {
        await addPage(formData, imageFile);
        successToast("Page created.");
      }

      setFormData({ title: "", subtitle: "", content: "" });
      setImageFile(null);
      setPreviewImage(null);
      setEditingPage(null);
    } catch (error) {
      console.error("Error submitting page:", error);
      errorToast("An error occurred. Please try again.");
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      subtitle: page.subtitle,
      content: page.content,
    });
    setPreviewImage(page.image || null);
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete page?",
        text: "This page will be permanently removed.",
      })
    ) {
      try {
        await deletePage(id);
        successToast("Page deleted.");
      } catch (error) {
        console.error("Error deleting page:", error);
        errorToast("An error occurred. Please try again.");
      }
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      <h2 className="text-2xl font-bold text-gray-800">Manage Pages</h2>

      {/* Form to Add/Edit Page */}
      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow-md">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
          <p><small className="text-blue-600">Recommended size: 800x600px</small></p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
          <input
            type="text"
            name="subtitle"
            value={formData.subtitle}
            onChange={handleInputChange}
            placeholder="Enter Subtitle"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
          <p><small className="text-blue-600">Recommended size: 800x600px</small></p>
            accept="image/*"
            onChange={handleFileChange}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              className="mt-2 w-full h-48 object-cover rounded"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <MDEditor
            value={formData.content}
            onChange={handleContentChange}
            placeholder="Enter Page Content"
            className="bg-white"
          />
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          {editingPage ? "Update Page" : "Create Page"}
        </button>
      </form>

      {/* Display All Pages */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">All Pages</h3>
        <div className="space-y-4">
          {pages.map((page) => (
            <div
              key={page.id}
              className="bg-white p-4 rounded-lg shadow-md border border-gray-200 flex justify-between items-center"
            >
              <div>
                <h4 className="text-lg font-semibold text-gray-800">{page.title}</h4>
                <p className="text-sm text-gray-500">{page.subtitle}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(page)}
                  className="text-blue-600 hover:underline"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(page.id)}
                  className="text-red-600 hover:underline"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}